import { normalizePhone, type E164 } from '@selogate/core'

export type ContactProperty = 'name' | 'tel'

/**
 * **D346 (f) — a number's type, from a closed list of five, and nothing else.**
 *
 * Both contact-book leaves map the platform's own label onto these tokens before anything crosses
 * the bridge: Android's `Phone.TYPE` integer, iOS's labeled value compared against Apple's
 * `CNLabel…` constants. A custom label is `other`, and **its text is never emitted** — the phone's
 * free-text label is third-party content this app has never read. The token is worded by the app's
 * own catalogs, never by the phone's (which answer in the phone's language, not the app's).
 *
 * **Display only.** It reaches no request body: the bulk row's field list in
 * `infra/tests/web-build-hygiene.test.ts` is exact, and it has no place for this.
 */
export type PhoneKind = 'mobile' | 'home' | 'work' | 'main' | 'other'

/** The five tokens, and the only ones `contactListOf` accepts. A readonly tuple, never a `Set`. */
const PHONE_KINDS: readonly PhoneKind[] = ['mobile', 'home', 'work', 'main', 'other']

function isPhoneKind(value: unknown): value is PhoneKind {
  return (PHONE_KINDS as readonly unknown[]).includes(value)
}

export interface PickedContact {
  readonly name?: readonly string[]
  readonly tel?: readonly string[]
  /**
   * D346 — aligned 1:1 with `tel`, present only when the contact book supplied a well-formed list.
   * Chrome's picker and the one-off native picker supply none, so the PWA shows no types.
   */
  readonly telKinds?: readonly PhoneKind[]
}

interface ContactsManagerLike {
  getProperties(): Promise<readonly string[]>
  select(
    properties: readonly ContactProperty[],
    options: { readonly multiple: boolean },
  ): Promise<readonly PickedContact[]>
}

/**
 * **D167 — the native container's picker, reached through Capacitor's injected global.**
 *
 * The Web Contacts API exists in neither WKWebView nor Android WebView, so everything above is
 * simply false inside the shell and the control disappeared — on iOS it had never appeared at all,
 * Safari having never shipped a Contact Picker. What replaces it is the OS picker each platform
 * already has, presented by `native/`'s own plugin.
 *
 * **The one-off picker asks for no permission on either platform — and since D242 that sentence no
 * longer covers the whole module.** The `SeloGateContacts` path below is unchanged and still
 * permission-free on both: Apple's `CNContactPickerViewController` reference says the app *"does
 * not need access to the user's contacts"* and has access *"only to the user's final selection"*,
 * and Android's Contacts Provider guide says the intent path needs no permission because the
 * contacts app delegates a read of the one chosen URI. So the rationale line above the button —
 * *"share only their selected phone number"* — stays literally true wherever that picker is what
 * runs.
 *
 * **D242 makes it half false, deliberately and asymmetrically.** iOS multi-select is free — the
 * plural delegate hands back the user's own selection, with no `CNContactStore` and no plist key —
 * so iOS still declares nothing. Android ships no permission-free multi-select contact intent at
 * all, so the multi path there is a *second* plugin, `SeloGateContactBook`, which takes
 * `READ_CONTACTS` and reads the address book. `SeloGateContacts` is untouched and is the negative
 * control that keeps the single path permanently permission-free. **The asymmetry is a maintained
 * position, not an oversight** — do not harmonise the platforms later by giving iOS a book read.
 * Two things this comment no longer says: *"neither platform asks for a permission"* (a phrase
 * quoted elsewhere in the repository, and now true only of the one-off picker), and that P18 §5.6
 * is untouched — it is partially reversed on Android and untouched on iOS. **The prompt is scoped
 * to the multi path; the manifest declaration is not**, and appears on the Play listing for every
 * user regardless.
 *
 * **D340 overturned the paragraph above by name, and the reason was a fact it did not have.** Apple's
 * picker offers no search at all — no field on screen and none on a pull-down, and no API to add
 * one — so an iPhone manager inviting a building scrolled to every name while Android's manager
 * typed into `ContactBookSheet`. Search needs a list the app renders, and a list the app renders is
 * a list the app has read, so iOS now takes the book too: `SeloGateContactBook` has a Swift leaf
 * under the same JS name, behind `NSContactsUsageDescription`. **What it bought is search parity,
 * not multi-select**, which iOS already had — and still has on the fallback: a manager who declines
 * the dialog gets Apple's plural picker, not Android's one-at-a-time intent (`osPickerMultiSelects`).
 * `SeloGateContacts` is still untouched on both platforms, and is still the negative control that
 * keeps the single path — the install owner step — permanently permission-free.
 *
 * **Read the injected global, never an import.** `web/` must not depend on `@capacitor/core`: the
 * shell is P6's and the container is P18's, and `native/` consumes the built bundle rather than the
 * other way round. `pwa/nativeContainer.ts` and `auth/nativeHttp.ts` say the same thing about the
 * same global for the same reason.
 *
 * **`nativePromise`, not `Capacitor.Plugins.SeloGateContacts`.** The bridge injects `nativePromise`
 * itself before app code runs; the `Plugins` proxies are built by `@capacitor/core`'s
 * `registerPlugin` at *import* time, so in a bundle that refuses that import the namespace is empty
 * and the obvious-looking call is `undefined`. `nativeHttp.ts` records the same trap.
 */
interface CapacitorBridge {
  /** D242 — which multi-select the platform can offer, read rather than discovered by rejection. */
  getPlatform?: () => string
  isNativePlatform?: () => boolean
  nativePromise?: (pluginName: string, methodName: string, options: unknown) => Promise<unknown>
}

const NATIVE_PLUGIN = 'SeloGateContacts'
/** D242 — the second Android plugin, registered separately so the first one keeps its refusals. */
const CONTACT_BOOK_PLUGIN = 'SeloGateContactBook'

function nativeBridge(): CapacitorBridge | undefined {
  const capacitor = (globalThis as typeof globalThis & { Capacitor?: CapacitorBridge }).Capacitor
  if (typeof capacitor?.nativePromise !== 'function') return undefined
  if (typeof capacitor.isNativePlatform !== 'function' || capacitor.isNativePlatform() !== true) {
    return undefined
  }
  return capacitor
}

/**
 * Read the plugin's answer into the shape the rest of this module already speaks.
 *
 * **A malformed answer is `failed`, never an invented contact.** Both plugins resolve `cancelled`
 * explicitly, so an object carrying neither that flag nor a phone list means the bridge is not what
 * we think it is — and defaulting it would hand `contactPhoneSelections` an empty selection that
 * renders as "that contact had no valid number", blaming the resident's address book for our bug.
 */
export function nativeContactOf(raw: unknown): ContactPickerResult {
  if (typeof raw !== 'object' || raw === null) return { kind: 'failed' }
  const answer = raw as { cancelled?: unknown; name?: unknown; phones?: unknown; contacts?: unknown }
  if (answer.cancelled === true) return { kind: 'cancelled' }

  // **D242 — two shapes, one parser, no merge.** iOS's plural delegate answers with `contacts`, and
  // where that key is present the top-level `phones` is not consulted at all (and vice versa): a
  // payload carrying both is a bridge we do not recognise, and merging the two would invent a
  // selection out of the disagreement.
  if (answer.contacts !== undefined) {
    const parsed = contactListOf(answer.contacts)
    if (parsed.malformed) return { kind: 'failed' }
    // Same reading as the single shape below: nothing usable is a cancel, never an empty selection.
    return parsed.contacts.length === 0
      ? { kind: 'cancelled' }
      : { kind: 'selected', contacts: parsed.contacts }
  }

  const tel = Array.isArray(answer.phones)
    ? answer.phones.filter((value): value is string => typeof value === 'string')
    : undefined
  if (tel === undefined) return { kind: 'failed' }
  // A contact with no numbers at all is not a selection this flow can use. `cancelled` rather than
  // `selected` with an empty list, so the caller's own empty-selection branch keeps meaning "the
  // contact had numbers and none of them parsed".
  if (tel.length === 0) return { kind: 'cancelled' }

  const name = typeof answer.name === 'string' && answer.name.trim().length > 0 ? answer.name : undefined
  return { kind: 'selected', contacts: [{ ...(name === undefined ? {} : { name: [name] }), tel }] }
}

type ParsedContactList =
  { readonly malformed: true } | { readonly malformed: false; readonly contacts: readonly PickedContact[] }

/**
 * D242 — the list both multi-select answers carry, read once for iOS's picker and Android's book.
 *
 * **Malformed is not the same as unusable, and the two verdicts are opposite.** A row that is not
 * an object, or whose `phones` is not a list, fails the *whole* payload: silently skipping it would
 * show eighteen rows for twenty taps and blame the manager's address book for a bridge fault, which
 * is the same lie `nativeContactOf`'s single path refuses one contact at a time. A row that is
 * well-formed and carries nothing this flow can dial is simply **dropped** — that is an ordinary
 * address-book entry, not a fault — and if every row drops the caller is told `cancelled`, so its
 * own empty-selection branch keeps meaning *"they had numbers and none of them parsed"*.
 */
function contactListOf(value: unknown): ParsedContactList {
  if (!Array.isArray(value)) return { malformed: true }
  const contacts: PickedContact[] = []
  for (const element of value) {
    if (typeof element !== 'object' || element === null) return { malformed: true }
    const row = element as { name?: unknown; phones?: unknown; phoneKinds?: unknown }
    if (!Array.isArray(row.phones)) return { malformed: true }
    const phones: readonly unknown[] = row.phones
    const kinds = phoneKindsOf(row.phoneKinds, phones.length)
    // Filtered as pairs, so a non-string number drops its own kind and the two lists stay aligned.
    const tel: string[] = []
    const telKinds: PhoneKind[] = []
    phones.forEach((phone, index) => {
      if (typeof phone !== 'string') return
      tel.push(phone)
      const kind = kinds?.[index]
      if (kind !== undefined) telKinds.push(kind)
    })
    if (tel.length === 0) continue
    const name = typeof row.name === 'string' && row.name.trim().length > 0 ? row.name : undefined
    contacts.push({
      ...(name === undefined ? {} : { name: [name] }),
      tel,
      ...(kinds === undefined ? {} : { telKinds }),
    })
  }
  return { malformed: false, contacts }
}

/**
 * **D346 (f) — deliberately looser than `phones`, and the asymmetry is the rule.** A malformed
 * `phones` fails the whole payload (D242: *malformed is not unusable*), because a skipped row
 * blames the manager's address book for a bridge fault. A type is display only, so a `phoneKinds`
 * that is absent, the wrong length, or carries a token outside the five keeps the contact **without
 * kinds** — the book never fails over a label. 🔸 The cost is that a leaf that stops emitting (or
 * renames the key) reads as a book with no types, silently; the two native guard copies requiring
 * the mapping constants on each platform are what stand in front of that.
 */
function phoneKindsOf(value: unknown, length: number): readonly PhoneKind[] | undefined {
  if (!Array.isArray(value) || value.length !== length) return undefined
  const kinds: readonly unknown[] = value
  return kinds.every(isPhoneKind) ? (kinds as readonly PhoneKind[]) : undefined
}

interface ContactPickerEnvironment {
  readonly manager?: ContactsManagerLike
  readonly hasContactsManagerConstructor: boolean
  /** D167 — present only inside the native container, and it takes precedence over the web API. */
  readonly native?: CapacitorBridge
}

export type ContactPickerResult =
  | { readonly kind: 'selected'; readonly contacts: readonly PickedContact[] }
  | { readonly kind: 'cancelled' }
  | { readonly kind: 'unavailable' }
  | { readonly kind: 'failed' }

export interface ContactPhoneSelection {
  readonly name?: string
  readonly phones: readonly E164[]
  /**
   * D346 — aligned 1:1 with `phones`, present only when the contact carried `telKinds`. When two raw
   * numbers normalize to one (`050-…` and `+97250…`), the **first** one's kind is kept, the same
   * first-wins rule the Android leaf applies to a duplicate raw number.
   */
  readonly kinds?: readonly PhoneKind[]
}

/**
 * D242 — which of the two multi-select surfaces this launch has.
 *
 * **Named for what it costs, not for where it renders.** `'address-book-read'` is a read of the
 * whole book behind `READ_CONTACTS` (Android) or the Contacts grant (iOS, D340); `'os-picker'` is
 * the out-of-process picker that costs nothing.
 * `'in-app'` would have named the rendering and invited someone to wire the book into
 * `ContactPickerBlock`, whose install step must stay permission-free.
 */
export type ContactPickerMode = 'os-picker' | 'address-book-read'

/**
 * **Takes `multiple` so `ContactPickerBlock`'s call site cannot receive `'address-book-read'`.**
 * The install owner step asks for one contact, and a single-select caller must be structurally
 * unable to reach the permission — a rule the type system enforces here rather than a convention a
 * later reader has to know. `undefined` is *no contact surface at all*: a browser with no
 * `navigator.contacts`, where the control does not appear.
 *
 * **The platform is read, never discovered by rejection** — `getPlatform() === 'android'`, the same
 * shape as `auth/nativeHttp.ts`'s iOS test and `ble/nativeBluetooth.ts`'s bridge read. Probing by
 * calling the plugin and reading the refusal would make silence the evidence, which this repository
 * has paid for more than once. **D340 widened the multi answer to iOS**, because its picker has no
 * search; a bridge that answers neither platform name is still `'os-picker'`, silence not being
 * evidence of either.
 */
export function contactPickerMode(o: { readonly multiple: boolean }): ContactPickerMode | undefined {
  const environment = browserEnvironment()
  const native = environment.native
  if (native !== undefined) {
    const platform = nativePlatform(native)
    return o.multiple && (platform === 'android' || platform === 'ios') ? 'address-book-read' : 'os-picker'
  }
  // The Web Contacts API is multi-select already, so a browser never needs the second mode.
  return environment.manager !== undefined && environment.hasContactsManagerConstructor
    ? 'os-picker'
    : undefined
}

function nativePlatform(native: CapacitorBridge): string | undefined {
  return typeof native.getPlatform === 'function' ? native.getPlatform() : undefined
}

/**
 * D340 — whether the permission-free OS picker on this platform can take several contacts, which
 * is what the screen's fallback asks after a book it could not read.
 *
 * **Only Android's cannot.** Its `ACTION_PICK` intent is single-row by construction, so asking it
 * for several would promise a sweep it cannot make; iOS's plural delegate is free and stays the
 * fallback when a manager declines the Contacts dialog, and the Web Contacts API is multi-select
 * already. Read from the platform, like `contactPickerMode`, never discovered by calling.
 */
export function osPickerMultiSelects(): boolean {
  const native = browserEnvironment().native
  return native === undefined || nativePlatform(native) !== 'android'
}

export type ContactBookResult =
  | {
      kind: 'read'
      contacts: readonly PickedContact[]
      truncated: boolean
      /**
       * D340 — iOS 18+ only: the manager shared **selected** contacts rather than the whole book,
       * so the list must say so or twelve contacts read as a broken address book. `false`
       * everywhere else, Android included, which has no such answer.
       */
      limited: boolean
    }
  | { kind: 'denied' }
  | { kind: 'cancelled' }
  | { kind: 'unavailable' }
  | { kind: 'failed' }

/**
 * D242 — the address-book read, the permission path, and the only caller of the second plugin.
 * Android's since D242, and the iPhone's since D340 — the same wire on both.
 *
 * **`denied` is a first-class answer, not an error.** The plugin reads `getPermissionState` before
 * it prompts, so a manager who refused once is told so without a second dialog, and the screen
 * falls silently back to the one-off picker — a declined grant is a choice, not a failure, and gets
 * no error surface (D344's Settings offer sits beside the button, from the peek, never here). `unavailable` means there is no container or no bridge; `failed` means the
 * bridge answered something we do not recognise, which is never rendered as an empty address book.
 *
 * **Nothing here stores anything.** The book crosses the bridge once, into the caller's component
 * state, and dies with it — never a module-level cache, which is the reflexive "don't read it
 * twice" optimization and is forbidden by name. `infra/tests/web-build-hygiene.test.ts` is what
 * holds this file to it, and the compliance no-moves rest on that guard rather than on this comment.
 */
export async function readContactBook(): Promise<ContactBookResult> {
  const bridge = nativeBridge()
  const call = bridge?.nativePromise
  if (bridge === undefined || typeof call !== 'function') return { kind: 'unavailable' }
  try {
    return contactBookOf(await call.call(bridge, CONTACT_BOOK_PLUGIN, 'readContacts', {}))
  } catch {
    // The plugin resolves a denial and a cancel; a rejection is a real fault, and the caller's own
    // fallback to the single picker is what the manager sees.
    return { kind: 'failed' }
  }
}

/** The book's answer, read through the same list parser the plural picker uses. */
function contactBookOf(raw: unknown): ContactBookResult {
  if (typeof raw !== 'object' || raw === null) return { kind: 'failed' }
  const answer = raw as {
    denied?: unknown
    cancelled?: unknown
    contacts?: unknown
    truncated?: unknown
    limited?: unknown
  }
  if (answer.denied === true) return { kind: 'denied' }
  if (answer.cancelled === true) return { kind: 'cancelled' }
  const parsed = contactListOf(answer.contacts)
  if (parsed.malformed) return { kind: 'failed' }
  // All dropped, or an empty book: nothing to select from, and a list surface over zero rows is
  // worse than not opening one. `truncated` cannot matter here — there is nothing to have cut.
  //
  // **D341 — except under a limited grant, where the empty list is the way to grow it.** The
  // emptiness is the grant's, not the book's, and the list is where the way to share more is
  // reached from — Apple's button under D341, the Settings link since D343 — so an empty limited
  // list is how the first contact gets shared. D340's same-day `none-shared` answer sent this case to Apple's picker
  // instead, which reaches the whole book once and leaves the grant as empty as it found it.
  if (parsed.contacts.length === 0 && answer.limited !== true) return { kind: 'cancelled' }
  // `limited` is read the way `truncated` is — only an explicit `true` counts — so a bridge that
  // omits it (Android, or an iOS before 18) shows the list with no caveat rather than a false one.
  return {
    kind: 'read',
    contacts: parsed.contacts,
    truncated: answer.truncated === true,
    limited: answer.limited === true,
  }
}

/**
 * D343 — the way iOS 18's limited grant grows: this app's page in the Settings app, where the
 * manager edits which contacts are shared or allows them all. D341 reached the same end from inside
 * the list through Apple's contact-access button, and on iOS 26+ hardware that button — like
 * Apple's contact-access picker — renders blank or does nothing, an Apple defect with no fix
 * announced; so the manager is sent to the one surface that works.
 *
 * **It returns no contact.** `returned` means the app is in front again; the caller re-reads the
 * book through `readContactBook`, the one read this module has, under the same two fields and the
 * same parser.
 *
 * **Every failure is `unavailable`**, which the caller answers by doing nothing: no bridge, a
 * rejection, Settings that would not open, or a payload this module does not recognise. The list
 * the manager was looking at is still there.
 */
export type ContactSettingsResult = { kind: 'returned' } | { kind: 'unavailable' }

export async function openContactSettings(): Promise<ContactSettingsResult> {
  const bridge = nativeBridge()
  const call = bridge?.nativePromise
  if (bridge === undefined || typeof call !== 'function') return { kind: 'unavailable' }
  try {
    return contactSettingsOf(await call.call(bridge, CONTACT_BOOK_PLUGIN, 'openContactSettings', {}))
  } catch {
    return { kind: 'unavailable' }
  }
}

export function contactSettingsOf(raw: unknown): ContactSettingsResult {
  if (typeof raw !== 'object' || raw === null) return { kind: 'unavailable' }
  return (raw as { returned?: unknown }).returned === true ? { kind: 'returned' } : { kind: 'unavailable' }
}

/**
 * D250 — whether the address book can be read **without raising a dialog**. Asked, never
 * prompted.
 *
 * **`'granted'` is a positive answer, and since D340 so is iOS 18's `'limited'`** — the manager
 * shared some contacts, a read of that subset raises no dialog, and raising none is the whole of
 * D250's test for the auto-open. Nothing else is positive. No container, no bridge, a plugin that
 * is not there, a rejection, a payload we do not recognise, and every Android permission state that
 * is not a grant all read `'ask'`. **The failure direction is D242's own behaviour** — the tap this
 * screen has required since D242 — so a bridge that stops answering costs the auto-open and can
 * never cost the prompt its user action. **D344's third answer, `'refused'`, is not positive
 * either**: it opens nothing and only draws the invite screen's Settings line (see
 * `contactBookAccessOf`).
 *
 * **It calls Capacitor's inherited `checkPermissions`, not a method of ours.** On Android `Plugin`
 * auto-implements that from `SeloGateContactBookPlugin`'s `@Permission` annotation; on iOS the Swift
 * leaf overrides `CAPPlugin`'s and answers the same key. The alias string below is the whole of the
 * wire contract, and `native/src/platformRequirements.ts` pins the same literal in both native
 * files — they agree by assertion rather than by memory. A method of our
 * own would have been a second entry point into a class whose only method *requests* a permission,
 * on a path whose entire purpose is not to.
 *
 * **Nothing calls this outside `'address-book-read'` mode.** Every browser answers `'os-picker'`,
 * where there is no permission to peek at and no auto-open was ever suppressed.
 */
export type ContactBookAccess = 'granted' | 'ask' | 'refused'

/** `SeloGateContactBookPlugin.CONTACT_BOOK_ALIAS`, and the only string this read depends on. */
const CONTACT_BOOK_ALIAS = 'contactBook'

/** The states that read without a dialog: a grant, and D340's iOS 18 limited grant. */
const READABLE_WITHOUT_PROMPT: readonly unknown[] = ['granted', 'limited']

/**
 * **D344 — `'refused'` is the iPhone manager's own *None*, and nothing else.** It is what earns the
 * invite screen's Settings line, so it is answered only where Settings can reverse it: the Swift
 * leaf's `'denied'`, which since D344 no longer covers `.restricted` (a Screen Time or
 * device-management block, answered `'restricted'` and read here as `'ask'`). **Android's
 * `'denied'` stays `'ask'`** — Capacitor's inherited state there does not reliably tell a permanent
 * refusal from a question never asked, and a line inviting a manager to Settings over a dialog the
 * next tap would raise is the wrong sentence. The platform is passed in, never discovered.
 */
export function contactBookAccessOf(raw: unknown, platform?: string): ContactBookAccess {
  if (typeof raw !== 'object' || raw === null) return 'ask'
  const state = (raw as Record<string, unknown>)[CONTACT_BOOK_ALIAS]
  if (READABLE_WITHOUT_PROMPT.includes(state)) return 'granted'
  return state === 'denied' && platform === 'ios' ? 'refused' : 'ask'
}

export async function contactBookAccess(): Promise<ContactBookAccess> {
  const bridge = nativeBridge()
  const call = bridge?.nativePromise
  if (bridge === undefined || typeof call !== 'function') return 'ask'
  try {
    return contactBookAccessOf(
      await call.call(bridge, CONTACT_BOOK_PLUGIN, 'checkPermissions', {}),
      nativePlatform(bridge),
    )
  } catch {
    return 'ask'
  }
}

function browserEnvironment(): ContactPickerEnvironment {
  const contactNavigator = navigator as Navigator & { readonly contacts?: ContactsManagerLike }
  const contactWindow = window as Window & { readonly ContactsManager?: unknown }
  const native = nativeBridge()
  return {
    ...(contactNavigator.contacts === undefined ? {} : { manager: contactNavigator.contacts }),
    hasContactsManagerConstructor: contactWindow.ContactsManager !== undefined,
    ...(native === undefined ? {} : { native }),
  }
}

export async function contactPickerProperties(
  environment: ContactPickerEnvironment = browserEnvironment(),
): Promise<readonly ContactProperty[] | undefined> {
  // **The native branch answers without probing.** `getProperties()` exists to discover what a
  // browser's picker will hand back; the container's plugin has one method and always returns a
  // name and a phone list, so there is nothing to ask and no failure mode to catch.
  if (environment.native !== undefined) return ['name', 'tel']
  if (environment.manager === undefined || !environment.hasContactsManagerConstructor) return undefined
  try {
    const available = await environment.manager.getProperties()
    if (!available.includes('tel')) return undefined
    return available.includes('name') ? ['name', 'tel'] : ['tel']
  } catch {
    return undefined
  }
}

/** Opens the one-off OS picker. Nothing here reads or persists an address book permission. */
export async function pickContacts(
  options: { readonly multiple: boolean; readonly properties: readonly ContactProperty[] },
  environment: ContactPickerEnvironment = browserEnvironment(),
): Promise<ContactPickerResult> {
  if (environment.native !== undefined) return pickNativeContact(environment.native, options.multiple)

  const { manager } = environment
  if (manager === undefined || !environment.hasContactsManagerConstructor) {
    return { kind: 'unavailable' }
  }

  try {
    if (!options.properties.includes('tel')) return { kind: 'unavailable' }
    // Keep select() directly in the click's user activation; property discovery happened earlier.
    const contacts = await manager.select(options.properties, { multiple: options.multiple })
    return contacts.length === 0 ? { kind: 'cancelled' } : { kind: 'selected', contacts }
  } catch (error) {
    return errorName(error) === 'AbortError' ? { kind: 'cancelled' } : { kind: 'failed' }
  }
}

/**
 * **No user-activation dance on native, and since D242 `multiple` is carried rather than dropped.**
 *
 * The browser branch above must keep `select()` inside the click's own activation, which is why
 * availability is discovered on mount rather than at the tap. A native plugin has no such rule —
 * the picker is a view controller, not a gesture-gated web API.
 *
 * D167 dropped the flag because neither OS picker was configured for more than one, and passing it
 * would have implied a choice the plugins did not offer. **iOS now offers it** — one plugin, two
 * delegate classes, chosen from this flag — so the honest thing is to forward it. **Android's
 * plugin still answers with one contact whatever the flag says**, and that is deliberate rather
 * than unfinished: its multi-select is a different plugin behind a permission, reached through
 * `contactPickerMode` and `readContactBook`, and this path stays the permission-free one.
 */
async function pickNativeContact(bridge: CapacitorBridge, multiple: boolean): Promise<ContactPickerResult> {
  const call = bridge.nativePromise
  if (typeof call !== 'function') return { kind: 'unavailable' }
  try {
    return nativeContactOf(await call.call(bridge, NATIVE_PLUGIN, 'pickContact', { multiple }))
  } catch (error) {
    // The plugins resolve a cancel rather than rejecting it, so a rejection here is a real fault —
    // a device with no contacts app, or a bridge that lost the plugin. Both leave manual entry.
    return errorName(error) === 'AbortError' ? { kind: 'cancelled' } : { kind: 'failed' }
  }
}

export function contactPhoneSelections(contacts: readonly PickedContact[]): readonly ContactPhoneSelection[] {
  return contacts.flatMap((contact) => {
    const tel = contact.tel ?? []
    // Only a list aligned with `tel` is read; anything else is a contact without kinds.
    const telKinds = contact.telKinds?.length === tel.length ? contact.telKinds : undefined
    // Insertion-ordered, and a number already present keeps the kind it arrived with.
    const phones = new Map<E164, PhoneKind | undefined>()
    tel.forEach((raw, index) => {
      try {
        const phone = normalizePhone(raw, 'IL', 'phone')
        if (!phones.has(phone)) phones.set(phone, telKinds?.[index])
      } catch {
        // A contact may carry non-Israeli or malformed numbers; manual entry remains available.
      }
    })
    if (phones.size === 0) return []
    const name = contact.name?.find((value) => value.trim().length > 0)?.trim()
    const kinds = [...phones.values()].filter((kind): kind is PhoneKind => kind !== undefined)
    return [
      {
        ...(name === undefined ? {} : { name }),
        phones: [...phones.keys()],
        ...(telKinds === undefined ? {} : { kinds }),
      },
    ]
  })
}

function errorName(error: unknown): string | undefined {
  if (error instanceof DOMException) return error.name
  if (typeof error === 'object' && error !== null && 'name' in error && typeof error.name === 'string') {
    return error.name
  }
  return undefined
}
